// Convert image file to base64
// BUG #9 FIX: convertImageToBase64 RIMOSSA da questo file.
// Usava FileReader direttamente senza compressione, producendo immagini da 5-10MB
// che potevano superare il limite di 10MB per nodo di Firebase Realtime Database.
// Importa SEMPRE da '../utils/imageToBase64' che comprime a max 0.5MB/1200px:
//   import { convertImageToBase64 } from '../utils/imageToBase64';

import { datesOverlap as sharedDatesOverlap, getEffectiveEndDate as sharedGetEffectiveEndDate } from './bookingRules.generated';
import { CARGOS_MOTOVEICOLI, bookingDurationDays } from './cargosRules.generated';


/**
 * Data LOCALE in formato 'YYYY-MM-DD' (a differenza di toISOString(), che
 * converte in UTC: in Italia tra mezzanotte e le 01:00/02:00 restituisce il
 * giorno PRECEDENTE, e con `new Date('YYYY-MM-DD')` + setDate sbaglia il
 * giorno nei fusi a ovest di Greenwich).
 */
export const toLocalISODate = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// BUG FIX (timezone): 'new Date("YYYY-MM-DD")' viene interpretata come
// mezzanotte UTC (spec ISO 8601). Se poi la si legge con i "getter" locali
// (getDay/getDate/toLocaleDateString) su un browser con fuso orario negativo
// rispetto a UTC (es. America/New_York, UTC-4/-5), il giorno risulta
// spostato indietro di uno: '2025-06-15' viene mostrato/calcolato come
// 14/06/2025. Per date-only string (YYYY-MM-DD, es. dataInizio/dataFine,
// scadenzaPolizza) va invece costruita una data "locale pura" con
// new Date(anno, mese, giorno). Per timestamp completi (con orario) o
// oggetti Date già esistenti, il comportamento nativo resta invariato.
export const parseLocalDate = (dateInput) => {
  if (dateInput instanceof Date) return dateInput;
  if (!dateInput) return new Date(NaN);
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput);
  if (dateOnlyMatch) {
    const [, y, m, d] = dateOnlyMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(dateInput);
};

// Get display price for a car (minimum weekly pricing or base price)
export const getDisplayPrice = (car) => {
  if (car.weeklyPricing && Array.isArray(car.weeklyPricing)) {
    const validPrices = car.weeklyPricing.filter(p => p && !isNaN(parseFloat(p)));
    if (validPrices.length > 0) {
      const minPrice = Math.min(...validPrices.map(p => parseFloat(p)));
      return minPrice.toFixed(2);
    }
  }
  return car.prezzoGiornaliero || '0';
};

// Format date to Italian locale
export const formatDate = (dateString, options) => {
  if (!dateString) return '—';
  const d = parseLocalDate(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT', options);
};

// BUG FIX #4: calculateBookingPrice e il conteggio giorni nel modal erano sfasati.
// Il modal mostrava: Math.ceil((end - start) / 86400000) → es. dal 1 al 5 = 4 giorni
// Ma calculateBookingPrice iterava start <= end → 5 iterazioni = 5 giorni di prezzo
//
// REGOLA SCELTA (standard noleggio auto):
// - Il giorno di RITIRO si paga, il giorno di RICONSEGNA NON si paga
// - Dal 1 al 5 = 4 giorni (1,2,3,4) → coerente con Math.ceil((end-start)/86400000)
//
// Fix: il while ora usa currentDate < end (stretto) invece di currentDate <= end
export const calculateBookingPrice = (car, startDate, endDate) => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  let totalPrice = 0;
  const currentDate = new Date(start);

  // Itera fino al giorno PRIMA della fine (il giorno di riconsegna non si paga)
  while (currentDate < end) {
    const dayOfWeek = currentDate.getDay();
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    if (car.weeklyPricing && car.weeklyPricing[adjustedDay] && parseFloat(car.weeklyPricing[adjustedDay]) > 0) {
      totalPrice += parseFloat(car.weeklyPricing[adjustedDay]);
    } else {
      totalPrice += parseFloat(car.prezzoGiornaliero || 0);
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return totalPrice.toFixed(2);
};

// Check if date is in the past
export const isPastDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};


// Restituisce la data di fine effettiva per i controlli di disponibilità.
// Se lo stato è 'auto_in_riconsegna', l'auto NON è ancora stata confermata
// come riconsegnata dall'admin → blocca tutte le date future fino a conferma.
// In tutti gli altri casi bloccanti usa dataFine normalmente.
// Audit 2026-09-20, punto #6: implementazione spostata in shared/bookingRules.js
// (fonte unica condivisa con functions/index.js), qui solo re-esportata con lo
// stesso nome per non rompere gli import esistenti in tutta l'app.
export const getEffectiveEndDate = sharedGetEffectiveEndDate;

// Check if dates overlap.
// REGOLA AGGIORNATA: il giorno di riconsegna (end) è BLOCCANTE finché l'admin
// non conferma "Riconsegna Avvenuta". Solo dopo quella conferma lo slot torna
// disponibile (auto_riconsegnata non è in STATI_BLOCCANTI).
// Audit 2026-09-20, punto #6: idem, re-esportata da shared/bookingRules.js.
export const datesOverlap = sharedDatesOverlap;

// Get price for a specific date
export const getPriceForDate = (car, date) => {
  const d = parseLocalDate(date);
  const dayOfWeek = d.getDay();
  const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  if (car.weeklyPricing && car.weeklyPricing[adjustedDay] && parseFloat(car.weeklyPricing[adjustedDay]) > 0) {
    return parseFloat(car.weeklyPricing[adjustedDay]);
  }
  return parseFloat(car.prezzoGiornaliero || 0);
};

/**
 * Determina se una prenotazione / veicolo è soggetta all'obbligo di comunicazione
 * CaRGOS (Questura - Polizia di Stato) ai sensi dell'art. 17 D.L. 113/2018 (L. 132/2018)
 * e D.M. 29/10/2021.
 *
 * Criteri di legge:
 * 1. Solo noleggi a breve termine: durata <= 30 giorni (i noleggi > 30 gg sono a lungo termine,
 *    esenti da CaRGOS e registrati all'Anagrafe Nazionale Veicoli ex art. 94 c. 4-bis CdS).
 * 2. Solo veicoli a motore con targa valida (art. 54 c. 1 CdS: auto, moto, furgoni).
 *    Sono escluse biciclette, e-bike, monopattini o mezzi privi di targa.
 * 3. L'azienda deve avere CaRGOS abilitato (se specificato settings).
 */
export const isBookingSubjectToCargos = (booking, car = null, settings = null) => {
  if (!booking) return false;

  // 1. Se vengono forniti i settings dell'azienda, verifica se CaRGOS è attivo/soggetto
  if (settings && settings.azienda && settings.azienda.cargosSoggetta === false && !settings.cargosConfigured) {
    return false;
  }

  // 2. Controllo durata: se > 30 giorni è noleggio a lungo termine (ESENTE da CaRGOS per legge)
  //    Giorni di calendario (come il server): con le date locali il cambio dell'ora di fine ottobre
  //    faceva risultare di 31 giorni un noleggio di 30, e il pannello non lo inviava.
  if (bookingDurationDays(booking) > 30) return false;

  // 3. Controllo veicolo: deve essere un autoveicolo (non moto) con targa
  const tipo = (car?.tipoVeicolo || booking?.tipoVeicolo || '').toLowerCase();
  if (tipo === 'bici' || tipo === 'bicicletta' || tipo === 'monopattino' || tipo === 'ebike') {
    return false;
  }
  // Moto/scooter: esclusi dall'obbligo (D.M. 29/10/2021: «autoveicoli» = almeno quattro ruote, esclusi i motoveicoli)
  if (CARGOS_MOTOVEICOLI.includes(tipo)) {
    return false;
  }

  // Se car o booking specifica targa, deve esserci
  const targa = (car?.targa || booking?.targa || '').trim();
  if (car && !targa) {
    return false;
  }

  return true;
};

